import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCommercialUnitOptions } from "@/api/unitPricing";
import { getLocalizedError } from "@/lib/api";

/**
 * Quy đổi đơn vị của một sản phẩm, hiện NGAY TRONG màn hình sản phẩm.
 *
 * Khách nói 14/09/2026: "khách có thể tự cấu hình trong product". Đúng về
 * nguyên tắc — mỗi mã một kiểu quy đổi, không có quy tắc chung (hệ KHÔNG tự cho
 * rằng 1 Gross = 144 cái). Nhưng chỗ khai lại nằm ở một trang riêng trên menu,
 * nên tới 14/09/2026 vẫn là **0 / 1.463 sản phẩm thật** được khai, trong khi 430
 * mã đang để đơn vị `sets` mà không nói một set gồm mấy chi tiết.
 *
 * Bảng này KHÔNG nhân bản trình soạn hồ sơ quy đổi vào form sản phẩm. Trình soạn
 * đó có phiên bản (U0001, U0002…) và bước duyệt, có chủ ý: giá và quy đổi đổi
 * thì chứng từ cũ phải giữ nguyên số cũ. Dựng một ô nhập thứ hai ngay tại đây là
 * tạo nguồn sự thật thứ hai cho cùng một con số. Thay vào đó: hiện đúng hiện
 * trạng, và mở thẳng đường sang trình soạn với sản phẩm đã điền sẵn.
 */
/** `embedded`: nằm trong thẻ nhóm của biểu mẫu sản phẩm — thẻ đã có tiêu đề nên bỏ tiêu đề và khoảng trên của khối. */
export function ProductUnitProfilePanel({ productId, embedded = false }: { productId: string; embedded?: boolean }) {
  const { t } = useTranslation();
  const query = useCommercialUnitOptions(productId);
  const data = query.data?.data;
  if (query.isLoading) return null;
  /**
   * Không đọc được (ví dụ tài khoản không có quyền menu Đơn vị & bảng giá) thì vẫn hiện tiêu đề kèm lý do:
   * trả về `null` là bảng biến mất không dấu vết, đúng kiểu hỏng im lặng dự án này tránh (tìm ra 15/09/2026,
   * khi đó lý do là cổng dùng thử — cổng đó gỡ 18/09/2026).
   */
  if (!data)
    return (
      <section className={embedded ? undefined : "mt-6"}>
        {embedded ? null : (
          <h3 className="eyebrow mb-3 border-b border-line pb-1.5 text-ink-faint">
            {t("unitPricing.productPanelTitle")}
          </h3>
        )}
        <p className="text-sm text-ink-soft">{getLocalizedError(query.error, t, "unitPricing.error")}</p>
      </section>
    );
  const profile = data.profile;
  return (
    <section className={embedded ? undefined : "mt-6"}>
      {embedded ? null : (
        <h3 className="eyebrow mb-3 border-b border-line pb-1.5 text-ink-faint">
          {t("unitPricing.productPanelTitle")}
        </h3>
      )}
      {profile ? (
        <div className="space-y-3 text-sm">
          <p>
            {t("unitPricing.baseUnit")}: <span className="font-mono">{profile.definition.baseUnit}</span>
            {" · "}
            <span className="font-mono">{profile.version}</span>
          </p>
          {profile.definition.units.length > 0 ? (
            <ul className="space-y-1">
              {profile.definition.units.map((u) => (
                <li key={u.unit} className="font-mono tabular">
                  {u.alternateQuantity} {u.unit} = {u.baseQuantity} {profile.definition.baseUnit}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-ink-soft">{t("unitPricing.productPanelNoUnits")}</p>
          )}
          {/*
            Đơn vị gốc của hồ sơ lệch với đơn vị đang khai trên sản phẩm thì mọi
            phép quy đổi đều sai một cách im lặng — phải nói ra, không giấu.
          */}
          {!data.sourceCurrent && (
            <p role="alert" className="rounded-md bg-warn-tint p-2 text-warn">
              {t("unitPricing.productPanelStale")}
            </p>
          )}
          <Link className="text-brand underline" to={`/unit-pricing/${profile.id}`}>
            {t("unitPricing.productPanelOpen")}
          </Link>
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="text-ink-soft">{t("unitPricing.productPanelEmpty")}</p>
          <Link className="text-brand underline" to={`/unit-pricing/new?productId=${productId}`}>
            {t("unitPricing.productPanelCreate")}
          </Link>
        </div>
      )}
    </section>
  );
}
